import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Github, Twitter, Youtube, Twitch, Mail, ArrowUp } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { label: 'Home', action: () => scrollToSection('hero') },
    { label: 'Gaming Tech', action: () => scrollToSection('gaming-tech') },
    { label: 'Guides', action: () => scrollToSection('how-to') },
    { label: "What's Hot", action: () => scrollToSection('whats-hot') },
    { label: 'Contact', action: () => scrollToSection('contact') },
  ];

  const socialLinks = [
    { icon: <Twitch className="w-5 h-5" />, label: 'Twitch', href: '#' },
    { icon: <Youtube className="w-5 h-5" />, label: 'YouTube', href: '#' },
    { icon: <Twitter className="w-5 h-5" />, label: 'Twitter', href: '#' },
    { icon: <Github className="w-5 h-5" />, label: 'GitHub', href: '#' },
  ];

  return (
    <footer id="contact" className="bg-gradient-to-t from-dark-900 to-dark-800 border-t border-primary-500/20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-electric-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-electric-500 rounded-lg flex items-center justify-center">
                <Gamepad2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-futuristic font-bold text-white">Arkiest</h3>
                <p className="text-primary-400 text-sm">Gaming Technology Unleashed</p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
              Your ultimate destination for gaming technology insights, reviews, and tutorials. 
              Stay ahead of the curve with cutting-edge hardware analysis and expert guides.
            </p>
            
            {/* Newsletter Signup Mini */}
            <div className="flex space-x-2">
              <input
                type="email"
                placeholder="Quick subscribe"
                className="flex-1 px-4 py-2 bg-dark-800/50 border border-primary-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 text-sm"
              />
              <motion.button
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <motion.button
                    onClick={link.action}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-300 text-left"
                    whileHover={{ x: 5 }}
                  >
                    {link.label}
                  </motion.button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold text-white mb-6">Follow Us</h4>
            <div className="space-y-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  className="flex items-center space-x-3 text-gray-300 hover:text-primary-400 transition-colors duration-300 group"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-8 h-8 glass rounded-lg flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
                    {social.icon}
                  </div>
                  <span>{social.label}</span>
                </motion.a>
              ))}
            </div>

            {/* Back to Top Button */}
            <motion.button
              onClick={scrollToTop}
              className="mt-8 glass px-4 py-2 rounded-lg text-primary-400 hover:text-white hover:bg-primary-500/20 transition-all duration-300 flex items-center space-x-2"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowUp className="w-4 h-4" />
              <span>Back to Top</span>
            </motion.button>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-primary-500/20 pt-8 flex flex-col md:flex-row items-center justify-between"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            © {currentYear} Arkiest. All rights reserved. Gaming Technology Unleashed.
          </p>
          
          <div className="flex items-center space-x-6 text-sm">
            <motion.a
              href="#"
              className="text-gray-400 hover:text-primary-400 transition-colors"
              whileHover={{ y: -1 }}
            >
              Privacy Policy
            </motion.a>
            <motion.a
              href="#"
              className="text-gray-400 hover:text-primary-400 transition-colors"
              whileHover={{ y: -1 }}
            >
              Terms of Service
            </motion.a>
            <motion.a
              href="#"
              className="text-gray-400 hover:text-primary-400 transition-colors"
              whileHover={{ y: -1 }}
            >
              Cookie Policy
            </motion.a>
          </div>
        </motion.div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-50"></div>
      </div>
    </footer>
  );
};

export default Footer;
